import React, { useCallback, useState, forwardRef, useEffect } from 'react';
import { useMessageContext, useChannelStateContext, ReactionSelectorProps, useChatContext } from 'stream-chat-react';
import { ReactionResponse, GetReactionsAPIResponse } from 'stream-chat';
import { Upvote } from '../../assets/Upvote';
import { UpvoteGrey } from '../../assets/UpvoteGrey';
import './CustomReactionSelectors.css';
import { StreamChatGenerics } from '../../types';

const CustomReactionSelectors = forwardRef<HTMLDivElement, ReactionSelectorProps<any>>((_, ref) => {
  /**
   * Get message, channel, and client from Stream Chat contexts
   */
  const {
    message: { own_reactions: ownReactions = [], id: messageId },
  } = useMessageContext('CustomReactionSelector');
  const { channel } = useChannelStateContext('CustomReactionSelector');
  const { client } = useChatContext<StreamChatGenerics>();

  /**
   * STATE MANAGEMENT
   * - reactions: All reactions on this message
   * - upvoteCount: Total number of upvotes
   * - hasUpvoted: Whether current user has upvoted
   */
  const [reactions, setReactions] = useState<ReactionResponse<any>[]>(ownReactions || []);
  const [upvoteCount, setUpvoteCount] = useState<number>(0);
  const [hasUpvoted, setHasUpvoted] = useState<boolean>(false);

  /**
   * FUNCTION: fetchReactions
   * Fetches all reactions for this message from Stream API
   * Updates counts and current user's vote status
   * CALLED:
   * - On mount
   * - When reaction.new or reaction.deleted events fire
   */
  const fetchReactions = async () => {
    try {
      const response: GetReactionsAPIResponse<any> = await channel.getReactions(messageId, { limit: 10000 });
      setReactions(response.reactions);

      // Recalculate upvote count based on the latest reactions
      const totalUpvotes = response.reactions.filter((r) => r.type === 'thumbs_up').length;
      setUpvoteCount(totalUpvotes);

      const userId = client.user?.id;
      if (userId) {
        const userHasUpvoted = response.reactions.some(
          (reaction) => reaction.type === 'thumbs_up' && reaction.user.id === userId
        );
        setHasUpvoted(userHasUpvoted);
      }
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
  };

  /**
   * EFFECT: Setup real-time reaction sync
   * Fetches initial reactions and listens for changes
   * Updates UI when any user adds/removes reactions
   */
  useEffect(() => {
    fetchReactions();

    const handleNewReaction = async (event: { message?: { id: string } }) => {
      if (event.message?.id === messageId) {
        await fetchReactions();
      }
    };

    channel.on('reaction.new', handleNewReaction);
    channel.on('reaction.deleted', handleNewReaction);

    return () => {
      channel.off('reaction.new', handleNewReaction);
      channel.off('reaction.deleted', handleNewReaction);
    };
  }, [channel, messageId]);

  /**
   * Debounce timeout ref to prevent double-click reactions
   * Waits 300ms before processing click
   */
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  /**
   * FUNCTION: handleReaction
   * Toggles reaction when user clicks upvote button
   * BEHAVIOR:
   * - If already upvoted: Remove reaction (decrement count)
   * - If not upvoted: Add reaction (increment count)
   * - Debounced to prevent accidental double-clicks
   * - Updates local state optimistically for instant feedback
   */
  const handleReaction = useCallback(
    (reactionType: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Clear previous timeout if exists
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Debounce: wait 300ms before processing
      debounceTimeoutRef.current = setTimeout(async () => {
        const userId = client.user?.id;

        const hasReactedWithType = reactions.some(
          (reaction) => reaction.type === reactionType && reaction.user.id === userId
        );

        try {
          if (hasReactedWithType) {
            // Remove upvote
            await channel.deleteReaction(messageId, reactionType);

            setReactions((prevReactions) => prevReactions.filter((reaction) => reaction.type !== reactionType));
            setUpvoteCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
            setHasUpvoted(false);
          } else {
            // Add upvote
            await channel.sendReaction(messageId, { type: reactionType });

            setReactions((prevReactions) => [...prevReactions, { type: reactionType, user: { id: userId } }]);
            setUpvoteCount((prevCount) => prevCount + 1);
            setHasUpvoted(true);
          }
        } catch (error) {
          console.error('Error sending reaction:', error);
        }
      }, 300);
    },
    [reactions, messageId, client.user?.id, channel]
  );

  /**
   * Icon selection based on user's vote status
   * Filled thumbs up if user upvoted, gray if not
   */
  const customEmojis = hasUpvoted
    ? [{ type: 'thumbs_up', image: <Upvote /> }]
    : [{ type: 'thumbs_up', image: <UpvoteGrey /> }];

  /**
   * RENDER
   * Upvote button with icon and count
   * Adds 'highlighted' class when user has upvoted
   */
  return (
    <div ref={ref}>
      {customEmojis.map((emoji) => (
        <div
          key={emoji.type}
          onClick={(e) => handleReaction(emoji.type, e)}
          className={`upvote-btn ${hasUpvoted ? 'highlighted' : ''}`}
        >
          {emoji.image} {upvoteCount}
        </div>
      ))}
    </div>
  );
});

export default CustomReactionSelectors;